import { LabelTypography } from '../../../../../ui/components/LabelTypography';
import { OrganizationsSelection } from '../../../../../ui/features/organizations/components/OrganizationsPanel/components/OrganizationsSelection';
import { useContactManagementTranslation } from '../../../../../ui/hooks/useContactManagementTranslation';
import { buildGeneralTab, buildValidateTab, showWidgetEditDialog } from '@juvare/common-form-ui/WidgetEditDialog';
import { FormHelperErrorDescription } from '@juvare/common-form-ui/WidgetForm/Components';
import { handleWidgetUpdate, renderForm, validateCustom } from '@juvare/common-form-ui/WidgetForm/Utils';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import SquareIcon from '@mui/icons-material/Square';
import { Box, FormControl, FormControlLabel, FormLabel } from '@mui/material';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import React from 'react';
import { accessByOrganizationType, orgPermissions } from '../types';
const WidgetAccessByOrganization = (props) => {
    var _a, _b, _c;
    const { t } = useContactManagementTranslation();
    const value = props.getData();
    const error = props.error;
    const tableView = ((_a = props.parentProps) === null || _a === void 0 ? void 0 : _a.config.type) === 'form.dataGrid';
    const option = (value === null || value === void 0 ? void 0 : value.public) ? 'public' : 'organizations';
    return (React.createElement(FormControl, { variant: 'outlined', fullWidth: true, required: (_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.required, error: !!error },
        React.createElement(Box, null,
            props.config.label && !tableView && (React.createElement(FormLabel, { error: !!error, disabled: props.config.disabled }, props.config.label)),
            !props.config.hidePublicOption && (React.createElement(RadioGroup, { value: option ? option : null, onChange: (event) => {
                    props.setData({
                        public: event.target.value === 'public',
                        visibility: undefined,
                    });
                    renderForm(props, { changed: true });
                } },
                React.createElement(FormControlLabel, { value: 'public', control: React.createElement(Radio, null), label: t('forms.orgAccessWidget.public'), "data-test": 'public-radio' }),
                React.createElement(FormControlLabel, { value: 'organizations', control: React.createElement(Radio, null), label: t('forms.orgAccessWidget.selectOrgs'), "data-test": 'organizations-radio' }))),
            !(value === null || value === void 0 ? void 0 : value.public) && (React.createElement(OrganizationsSelection, { staticDialogFilters: {
                    orgPermissions: orgPermissions,
                }, showSelectAllChildren: true, organizationSelectionRequired: (_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.required, organizationIds: (value === null || value === void 0 ? void 0 : value.visibility) || [], onChangeOrganizationIds: (newOrganizationIds) => {
                    props.setData(Object.assign(Object.assign({}, value), { visibility: newOrganizationIds.length ? newOrganizationIds : undefined }));
                    renderForm(props, { changed: true, autoSaveImmediately: true });
                }, renderOrganization: (organization) => (React.createElement(React.Fragment, null,
                    React.createElement(LabelTypography, { sx: { flexGrow: 1 }, "aria-disabled": organization.disabled }, organization.name),
                    value.visibility.includes(organization.id) && (React.createElement(React.Fragment, null,
                        React.createElement(Box, { sx: { ml: 2, mr: 2 } }, t('forms.orgAccessWidget.shared')))))) })),
            React.createElement(FormHelperErrorDescription, { error: error, description: props.config.description }))));
};
const WidgetAccessByOrganizationEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, {
            title: props.config.label,
        }, buildGeneralTab(props, {
            key: true,
            label: true,
            description: true,
            disabled: true,
            children: [{ type: 'form.checkbox', key: 'hidePublicOption', label: 'Hide Public Option' }],
        }), buildValidateTab(props, { required: true, min: true, max: true })) }),
        React.createElement(WidgetAccessByOrganization, Object.assign({}, props))));
};
export const widgetAccessByOrganization = {
    type: accessByOrganizationType,
    createWidget: () => ({ type: accessByOrganizationType, key: 'access', label: 'Access' }),
    init: (props) => {
        const data = props.getData();
        if (data === null) {
            props.setData({
                public: true,
                visibility: undefined,
            });
        }
    },
    validate: (props) => {
        var _a, _b;
        const data = props.getData() || {};
        const publicSelected = data.public;
        const visibility = data.visibility || [];
        const publicOptionHidden = props.config.hidePublicOption;
        if (!publicOptionHidden && !publicSelected && visibility.length === 0) {
            return 'At least one organization must be selected';
        }
        else if (publicOptionHidden && visibility.length === 0) {
            return 'At least one organization must be selected';
        }
        if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) && !publicSelected) {
            if (!Array.isArray(visibility) || visibility.length === 0) {
                return 'At least one organization must be selected';
            }
        }
        const { minOrganizationsLength, maxOrganizationsLength } = (_b = props.config.validate) !== null && _b !== void 0 ? _b : {};
        if (minOrganizationsLength != null && Array.isArray(visibility)) {
            if (minOrganizationsLength === 1 && visibility.length === 0) {
                return 'At least one organization must be selected';
            }
            else if (minOrganizationsLength > 1 && visibility.length < minOrganizationsLength) {
                return 'At least ' + minOrganizationsLength + ' organizations must be selected';
            }
        }
        if (maxOrganizationsLength != null && Array.isArray(visibility)) {
            if (maxOrganizationsLength === 1 && visibility.length > 1) {
                return 'No more than one organization must be selected';
            }
            else if (maxOrganizationsLength > 1 && visibility.length > maxOrganizationsLength) {
                return 'A maximum of ' + maxOrganizationsLength + ' organizations must be selected';
            }
        }
        return validateCustom(props, visibility);
    },
    update: handleWidgetUpdate,
    component: WidgetAccessByOrganization,
    icon: React.createElement(SquareIcon, { fontSize: 'small' }),
    editorComponent: WidgetAccessByOrganizationEditor,
};
